/**
 * <modules-addons-v2> custom element for the V2 PDP "Add ons" grid.
 *
 * Owns every tile carrying `data-addon-toggle` inside a single
 * <modules-addons-v2> instance (snippets/modules-addons-v2.liquid renders
 * one per section; the modules_addons block has `"limit": 1`, so there is
 * only ever one instance per page). Tiles are plain toggle buttons (not
 * radios/checkboxes) since add-ons are independently selectable.
 *
 * State: a Map of variantId -> qty. Clicking a tile selects it (qty 1) and
 * reveals a −/+ stepper; the stepper adjusts qty, and decrementing below 1
 * deselects (removes) the tile. Any change to the SET of selected add-ons
 * (select/deselect/colour-reset — not qty) dispatches an `addon:gallery`
 * event carrying the full ordered list of selected add-ons
 * ({ variantId, image, label }), so product-gallery-v2 can render each
 * selected add-on's featured image as a navigable slide + thumbnail and
 * revert to the variant's own media when the list empties.
 *
 * COLOUR SYNC (final review fix): snippets/modules-addons-v2.liquid now
 * renders every module variant unconditionally, each carrying
 * `data-module-colour` (the module product's own option1 colour) and a
 * server-rendered `hidden` attribute for every tile that doesn't match the
 * page's initial colour. This element listens for the document-level
 * `variant:change` event dispatched by
 * assets/component-configuration-picker-v2.js on every main-product option
 * change, reads the new variant's `option2` (the MAIN product's colour
 * position — see snippets/modules-addons-v2.liquid's {% doc %} for why
 * this is a different option index than the module product's own colour
 * option, even though the value sets line up), and:
 *   1. toggles `hidden` on every `[data-addon-toggle]` tile by comparing
 *      its `data-module-colour` to the new colour;
 *   2. clears every current selection (_items, aria-pressed, the
 *      `v2-tile--selected` class) — a tile selected under the old colour
 *      is a different, now-hidden variant id, so it must not silently
 *      ride along in the add-to-cart payload;
 *   3. updates the `[data-addon-count]` span inside the "See all N add
 *      ons" link to the new colour's visible tile count;
 *   4. re-dispatches `addons:change` with the emptied state so
 *      buy-buttons-v2's ADD TO CART label resets to the base variant
 *      price.
 *
 * Public interface (consumed by Task 9's buy-buttons-v2):
 *   - window.V2Addons.getItems() -> [{ variantId, qty }, ...]
 *   - document CustomEvent `addons:change`, detail: { items, totalPrice }
 *     (totalPrice is the sum of selected tiles' data-price-cents — an
 *     integer number of cents, since Liquid's variant.price is already
 *     cents, so no float math is needed here).
 *
 * window.V2Addons.getItems is set to a `[]`-returning stub immediately so
 * it is safe to call even before this custom element has upgraded/
 * connected (e.g. from a script that loads and runs before the element is
 * parsed), then rebound to the live instance in connectedCallback.
 */
(function () {
  // Idempotency guard, same pattern as component-configuration-picker-v2.js.
  if (window.__v2ModulesAddonsBound) return;
  window.__v2ModulesAddonsBound = true;

  window.V2Addons = window.V2Addons || {
    getItems: function () {
      return [];
    },
  };

  class ModulesAddonsV2 extends HTMLElement {
    connectedCallback() {
      this._items = new Map();
      this._currentColour = this.dataset.initialColour || null;
      this._onClick = this._onClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);
      document.addEventListener('variant:change', this._onVariantChange);
      window.V2Addons.getItems = this.getItems.bind(this);
      // Apply the inline "first 4" limit for the initial colour (SSR already
      // renders it, but this keeps the JS state authoritative + idempotent).
      this._applyInlineLimit(this._currentColour);
      this._initTrunk();
    }

    // Trunk add-on tile (rendered inline by snippets/modules-addons-v2.liquid):
    // a single always-visible 2-D picker whose active variant is (current sofa
    // colour × selected Tray-top). Parse its embedded variant map and reconcile
    // the SSR tile with the initial colour.
    _initTrunk() {
      this._trunkTile = this.querySelector('[data-trunk]');
      this._trunkMap = [];
      this._trunkTray = '';
      if (!this._trunkTile) return;
      var json = this._trunkTile.querySelector('[data-trunk-variants]');
      if (json) {
        try {
          this._trunkMap = JSON.parse(json.textContent) || [];
        } catch (e) {
          this._trunkMap = [];
        }
      }
      var checked = this._trunkTile.querySelector(
        '[data-trunk-tray-input][aria-checked="true"]',
      );
      this._trunkTray = (
        checked && checked.dataset.value ? checked.dataset.value : ''
      ).toLowerCase();
      this._resolveTrunk();
    }

    // Tray-top swatch chosen: move the radiogroup selection, update the label,
    // and re-resolve the active trunk variant (which also swaps the cart
    // payload when the tile is already selected).
    _onTraySelect(swatch) {
      var tile = this._trunkTile;
      if (!tile) return;
      tile.querySelectorAll('[data-trunk-tray-input]').forEach(function (s) {
        var on = s === swatch;
        s.setAttribute('aria-checked', on ? 'true' : 'false');
        s.classList.toggle('ma-v2__tray-swatch--selected', on);
      });
      var label = tile.querySelector('[data-trunk-tray-value]');
      if (label) label.textContent = swatch.dataset.value || '';
      this._trunkTray = (swatch.dataset.value || '').toLowerCase();
      this._resolveTrunk();
      this._dispatchChange();
      this._syncGallery();
    }

    // Resolve the trunk's active variant from (current sofa colour × selected
    // tray) and reflect it on the tile: variant id, price, image, availability
    // — and keep the add-to-cart payload in sync when the id changes under a
    // live selection. Hides the tile when the sofa colour has no trunk variant.
    _resolveTrunk() {
      var tile = this._trunkTile;
      if (!tile || !this._trunkMap.length) return;
      var colour = (this._currentColour || '').toLowerCase();
      var tray = this._trunkTray;
      var match = null;
      for (var i = 0; i < this._trunkMap.length; i++) {
        var e = this._trunkMap[i];
        var colourOk = e.colour === '' || e.colour === colour;
        var trayOk = tray === '' || e.tray === tray;
        if (colourOk && trayOk) {
          match = e;
          break;
        }
      }

      var prevId = tile.dataset.variantId;
      var wasSelected = tile.getAttribute('aria-pressed') === 'true';
      var qty = this._items.get(prevId) || 1;

      if (!match) {
        tile.hidden = true;
        if (wasSelected) {
          this._items.delete(prevId);
          tile.setAttribute('aria-pressed', 'false');
          tile.classList.remove('v2-tile--selected');
        }
        return;
      }

      tile.hidden = false;
      tile.disabled = !match.avail;
      tile.dataset.variantId = match.id;
      tile.dataset.priceCents = match.cents;
      tile.dataset.addonImage = match.image;

      var img = tile.querySelector('[data-trunk-image]');
      if (img && (match.thumb || match.image)) img.src = match.thumb || match.image;
      var priceEl = tile.querySelector('[data-trunk-price]');
      if (priceEl) {
        priceEl.textContent = match.price;
        priceEl.classList.toggle('v2-price-sale', !!match.compare);
      }
      var compareEl = tile.querySelector('[data-trunk-compare]');
      if (compareEl) {
        compareEl.textContent = match.compare || '';
        compareEl.hidden = !match.compare;
      }
      var saveEl = tile.querySelector('[data-trunk-save]');
      if (saveEl) {
        saveEl.textContent = match.save ? 'Save ' + match.save : '';
        saveEl.hidden = !match.save;
      }

      if (wasSelected && String(prevId) !== String(match.id)) {
        this._items.delete(prevId);
        this._items.set(String(match.id), qty);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('variant:change', this._onVariantChange);
    }

    _onVariantChange(event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;

      // Read the colour from whichever option index is the Colour option on
      // this product (data-colour-option, 1-based; set by the snippet).
      // Falls back to option2 for the legacy model.
      var colourPos = parseInt(this.dataset.colourOption, 10) || 2;
      var colour = variant['option' + colourPos];
      // Only colour changes invalidate the module selection — arm/fabric
      // changes within the same colour must not wipe the shopper's add-ons.
      if (colour === this._currentColour) return;
      this._currentColour = colour;

      this._applyInlineLimit(colour);
      this._clearSelections();
      // The trunk body colour follows the sofa: re-match its active variant to
      // the new colour (swapping the cart payload if it was selected).
      this._resolveTrunk();
      this._dispatchChange();
      this._syncGallery(); // selection emptied → gallery drops any add-on slides
    }

    // Show only the first INLINE_LIMIT modules of `colour` in the inline grid;
    // the rest are "overflow" — kept in the DOM (so their cart state/toggle
    // still work via the drawer remotes) but hidden inline and flagged with
    // data-in-drawer="true" so the See-all drawer reveals exactly them. Also
    // syncs the "See all N add ons" count + its visibility.
    _applyInlineLimit(colour) {
      // Combined inline count is editor-set (data-inline-limit, Trunk included,
      // default 4). The module-only limit subtracts the always-inline tiles
      // (the Trunk), so the Trunk counts as one of the N — mirrors the server's
      // module_inline_limit in snippets/modules-addons-v2.liquid. Floored at 0.
      var combined = parseInt(this.dataset.inlineLimit, 10);
      if (isNaN(combined)) combined = 4;
      var always = this.querySelectorAll('[data-addon-always]').length;
      var limit = Math.max(0, combined - always);
      var matching = 0;
      this.querySelectorAll('[data-addon-toggle]').forEach(function (tile) {
        // Always-visible tiles (the trunk add-on, data-addon-always) are a
        // SEPARATE product with no colour variant of their own: exempt from
        // colour gating (they occupy a fixed inline slot counted above via
        // `always`) — never hidden here, never overflow, never counted here,
        // never in the See-all drawer.
        if (tile.dataset.addonAlways === 'true') {
          tile.hidden = false;
          tile.dataset.inDrawer = 'false';
          return;
        }
        var matches = tile.dataset.moduleColour === colour;
        var overflow = false;
        if (matches) {
          matching += 1;
          if (matching > limit) overflow = true;
        }
        tile.hidden = !matches || overflow;
        // Every current-colour tile is listed in the "See all modules" drawer
        // (not just the inline overflow), so its remote must be revealed.
        tile.dataset.inDrawer = matches ? 'true' : 'false';
      });

      var overflowCount = Math.max(0, matching - limit);
      var seeAll = this.querySelector('[data-addon-see-all]');
      if (seeAll) {
        seeAll.hidden = overflowCount === 0;
      }
    }

    _clearSelections() {
      var self = this;
      this.querySelectorAll('[data-addon-toggle]').forEach(function (tile) {
        // Preserve always-visible tiles (trunk): their variant id doesn't
        // change with the sofa's colour, so a colour change must NOT drop them
        // from the add-to-cart payload or reset their quantity. _resolveTrunk
        // (called right after) re-matches the trunk to the new colour.
        if (tile.dataset.addonAlways === 'true') return;
        tile.setAttribute('aria-pressed', 'false');
        tile.classList.remove('v2-tile--selected');
        var qty = tile.querySelector('[data-addon-qty]');
        if (qty) qty.textContent = '1';
        self._items.delete(tile.dataset.variantId);
      });
    }

    _onClick(event) {
      // Stepper controls sit inside the tile and bubble here first: a −/+
      // click adjusts quantity and must NOT toggle the tile's selection.
      var dec = event.target.closest('[data-addon-dec]');
      var inc = event.target.closest('[data-addon-inc]');
      var tile = event.target.closest('[data-addon-toggle]');
      if (!tile || !this.contains(tile) || tile.disabled) return;

      // Trunk Tray-top swatch (role="radio" span inside the tile <button>):
      // handle the finish change and do NOT toggle the tile's selection.
      var tray = event.target.closest('[data-trunk-tray-input]');
      if (tray) {
        this._onTraySelect(tray);
        return;
      }

      var variantId = tile.dataset.variantId;
      if (!variantId) return;

      if (dec) {
        this._changeQty(tile, variantId, -1);
        return;
      }
      if (inc) {
        this._changeQty(tile, variantId, 1);
        return;
      }

      var pressed = tile.getAttribute('aria-pressed') === 'true';
      if (pressed) {
        this._deselect(tile, variantId);
      } else {
        this._select(tile, variantId);
      }
    }

    // Enter/Space on a role="button" stepper span (spans have no native
    // activation) — route it through the same click path.
    _onKeydown(event) {
      if (
        event.key !== 'Enter' &&
        event.key !== ' ' &&
        event.key !== 'Spacebar'
      )
        return;
      // Trunk Tray-top swatch spans have no native activation.
      var tray = event.target.closest('[data-trunk-tray-input]');
      if (tray && this.contains(tray)) {
        event.preventDefault();
        this._onTraySelect(tray);
        return;
      }
      var stepper = event.target.closest('[data-addon-dec],[data-addon-inc]');
      if (!stepper || !this.contains(stepper)) return;
      event.preventDefault();
      stepper.click();
    }

    _select(tile, variantId) {
      this._items.set(variantId, 1);
      tile.setAttribute('aria-pressed', 'true');
      tile.classList.add('v2-tile--selected');
      this._setQtyText(tile, 1);
      this._dispatchChange();
      this._syncGallery(); // add this add-on's image as a gallery slide
    }

    _deselect(tile, variantId) {
      this._items.delete(variantId);
      tile.setAttribute('aria-pressed', 'false');
      tile.classList.remove('v2-tile--selected');
      this._dispatchChange();
      this._syncGallery(); // drop this add-on's slide; revert if none left
    }

    // Stepper only acts on an already-selected tile. Below 1 → deselect.
    // Quantity does NOT change the gallery slide set, so no _syncGallery here.
    _changeQty(tile, variantId, delta) {
      if (!this._items.has(variantId)) return;
      var qty = (this._items.get(variantId) || 1) + delta;
      if (qty < 1) {
        this._deselect(tile, variantId);
        return;
      }
      this._items.set(variantId, qty);
      this._setQtyText(tile, qty);
      this._dispatchChange();
    }

    _setQtyText(tile, qty) {
      var el = tile.querySelector('[data-addon-qty]');
      if (el) el.textContent = String(qty);
    }

    // Ordered list of selected add-ons (Map preserves insertion order, so the
    // last-selected is last — the gallery scrolls to it). Each entry carries
    // the tile's featured-image URL + display name for the injected slide.
    _selectedAddons() {
      var self = this;
      var addons = [];
      this._items.forEach(function (qty, variantId) {
        var tile = self.querySelector(
          '[data-addon-toggle][data-variant-id="' + variantId + '"]',
        );
        if (!tile || !tile.dataset.addonImage) return;
        var nameEl = tile.querySelector('.ma-v2__tile-name');
        // Full gallery image set for this module (media.gallery_variant_images),
        // pipe-joined in data-addon-images; empties dropped. Falls back to the
        // single featured image. The gallery uses this list to REPLACE itself
        // with all of the selected module's images.
        var images = (tile.dataset.addonImages || '')
          .split('|')
          .filter(Boolean);
        if (!images.length) images = [tile.dataset.addonImage];
        addons.push({
          variantId: variantId,
          image: tile.dataset.addonImage,
          images: images,
          label: nameEl ? nameEl.textContent : '',
        });
      });
      return addons;
    }

    _syncGallery() {
      document.dispatchEvent(
        new CustomEvent('addon:gallery', {
          detail: { addons: this._selectedAddons() },
        }),
      );
    }

    getItems() {
      var items = [];
      this._items.forEach(function (qty, variantId) {
        items.push({ variantId: variantId, qty: qty });
      });
      return items;
    }

    _totalPrice() {
      var items = this._items;
      var total = 0;
      this.querySelectorAll('[data-addon-toggle]').forEach(function (tile) {
        var qty = items.get(tile.dataset.variantId);
        if (!qty) return;
        total += (parseInt(tile.dataset.priceCents, 10) || 0) * qty;
      });
      return total;
    }

    _dispatchChange() {
      document.dispatchEvent(
        new CustomEvent('addons:change', {
          detail: { items: this.getItems(), totalPrice: this._totalPrice() },
        }),
      );
    }
  }

  if (!customElements.get('modules-addons-v2')) {
    customElements.define('modules-addons-v2', ModulesAddonsV2);
  }

  /**
   * "See all N add ons" drawer remotes (snippets/modules-all-drawer-v2.liquid).
   * Each drawer card carries `data-addon-remote data-variant-id="…"` and is a
   * thin remote for the matching inline `[data-addon-toggle]` tile — clicking a
   * card forwards to the inline tile's real toggle logic, so the single
   * <modules-addons-v2> instance stays the sole owner of the add-to-cart state
   * (no second instance, no double-counting). The card then mirrors the inline
   * tile's hidden + selected state.
   */
  function syncAddonRemotes() {
    document.querySelectorAll('[data-addon-remote]').forEach(function (remote) {
      var tile = document.querySelector(
        '[data-addon-toggle][data-variant-id="' +
          remote.dataset.variantId +
          '"]',
      );
      if (!tile) {
        remote.hidden = true;
        return;
      }
      // Show a remote for every current-colour tile (flagged by data-in-drawer)
      // so the drawer lists all modules of the colour. A tile hidden merely for
      // being the wrong colour is data-in-drawer="false", so its remote hides too.
      remote.hidden = tile.dataset.inDrawer !== 'true';
      var on = tile.getAttribute('aria-pressed') === 'true';
      remote.setAttribute('aria-pressed', on ? 'true' : 'false');
      remote.classList.toggle('ma-v2-drawer__item--added', on);
      // Mirror the inline tile's quantity onto the card's stepper value.
      var tileQty = tile.querySelector('[data-addon-qty]');
      var remoteQty = remote.querySelector('[data-addon-remote-qty]');
      if (tileQty && remoteQty) remoteQty.textContent = tileQty.textContent;
    });
  }

  document.addEventListener('click', function (e) {
    var remote = e.target.closest('[data-addon-remote]');
    if (!remote || remote.disabled) return;
    var tile = document.querySelector(
      '[data-addon-toggle][data-variant-id="' + remote.dataset.variantId + '"]',
    );
    if (!tile) return;
    // Stepper −/+ on the card forward to the inline tile's own stepper (which
    // the <modules-addons-v2> element owns); a click anywhere else toggles.
    if (e.target.closest('[data-addon-remote-dec]')) {
      var decBtn = tile.querySelector('[data-addon-dec]');
      if (decBtn) decBtn.click();
      return;
    }
    if (e.target.closest('[data-addon-remote-inc]')) {
      var incBtn = tile.querySelector('[data-addon-inc]');
      if (incBtn) incBtn.click();
      return;
    }
    tile.click(); // drives the inline tile's select/deselect
  });

  // Enter/Space on a role="button" stepper span (spans have no native
  // activation) — route through the same click path.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    var step = e.target.closest(
      '[data-addon-remote-dec],[data-addon-remote-inc]',
    );
    if (!step) return;
    e.preventDefault();
    step.click();
  });

  // The inline element updates tile hidden/pressed on these events; re-sync the
  // remotes AFTER (rAF) so they reflect the just-updated inline state.
  document.addEventListener('addons:change', function () {
    window.requestAnimationFrame(syncAddonRemotes);
  });
  document.addEventListener('variant:change', function () {
    window.requestAnimationFrame(syncAddonRemotes);
  });

  // Initial sync once the inline element has applied its limit on connect.
  window.requestAnimationFrame(syncAddonRemotes);
})();
